/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package repository

import kotlinx.browser.window
import kotlinx.coroutines.flow.Flow
import kotlinx.datetime.LocalDate
import models.*
import studio.PaymentFrequency

/**
 * Get the base HTTP URL based on current environment
 * Uses localhost for development, empty string (same origin) for production
 */
private fun getBaseHttpUrl(): String {
    val hostname = window.location.hostname
    return if (hostname == "localhost" || hostname == "127.0.0.1") {
        "http://localhost:3000"
    } else {
        "" // Empty = same origin, nginx proxies /api/* to backend
    }
}

/**
 * Get the WebSocket URL based on current environment
 */
private fun getWebSocketUrl(): String {
    val hostname = window.location.hostname
    val protocol = if (window.location.protocol == "https:") "wss:" else "ws:"
    return if (hostname == "localhost" || hostname == "127.0.0.1") {
        "ws://localhost:3000"
    } else {
        "$protocol//${window.location.host}"
    }
}

/**
 * JavaScript/Web implementation of createSavingsGoalsRepository
 * Returns a sync-enabled repository that uses server as primary storage
 * Local database is used for offline caching
 *
 * DEPRECATED: Use createSavingsGoalsRepositoryWithAuth() instead to provide auth token
 * This version exists for backward compatibility but won't sync without auth
 */
actual fun createSavingsGoalsRepository(): SavingsGoalsRepository {
    throw IllegalStateException("⚠️ createSavingsGoalsRepository() called without auth - server sync will fail. Use createSavingsGoalsRepositoryWithAuth() instead.")
}

// SINGLETON instance to prevent multiple WebSocket connections
private var singletonRepository: WebSavingsGoalsRepositoryWrapper? = null

/**
 * Web-specific function to create repository with auth token support
 * This is the RECOMMENDED way to create a repository for Web - provides full server sync
 * Returns a SINGLETON to avoid multiple WebSocket connections
 */
fun createSavingsGoalsRepositoryWithAuth(
    getAuthToken: suspend () -> String? = { null },
    getUserId: suspend () -> String? = { null }
): SavingsGoalsRepository {
    if (singletonRepository == null) {
        console.log("🏗️ Creating SINGLETON WebSavingsGoalsRepositoryWrapper")
        singletonRepository = WebSavingsGoalsRepositoryWrapper(getAuthToken, getUserId)
    } else {
        console.log("♻️ Reusing existing SINGLETON WebSavingsGoalsRepositoryWrapper")
    }
    return singletonRepository!!
}

/**
 * Drop the cached singleton so the next createSavingsGoalsRepositoryWithAuth()
 * rebuilds under the current identity. Called on demo enter/exit so the goals
 * repo switches between the real user and the demo account.
 */
fun resetWebSavingsGoalsRepositorySingleton() {
    singletonRepository = null
    console.log("🔄 Web: Reset SINGLETON savings-goals repository")
}

/**
 * Wrapper that creates a sync-enabled repository with server backing
 */
private class WebSavingsGoalsRepositoryWrapper(
    private val getAuthToken: suspend () -> String? = { null },
    private val getUserId: suspend () -> String? = { null }
) : SavingsGoalsRepository {
    private var realRepository: SavingsGoalsRepository? = null
    private val userIdManager = auth.UserIdManager()

    /**
     * Reset the repository - forces reinitialization with fresh userId on next access
     * Call this after login/registration to pick up the new authenticated userId
     */
    fun reset() {
        console.log("🔄 WebWrapper: Resetting repository to pick up new userId")
        realRepository = null
    }

    private var isInitialPullComplete = false
    private var initializedUserId: String? = null

    private suspend fun getRepository(): SavingsGoalsRepository {
        val currentUserId = getUserId() ?: userIdManager.getUserId()
        
        // Check if userId has changed since last initialization
        if (realRepository != null && initializedUserId != currentUserId) {
            console.log("🔄 WebWrapper: UserId changed from $initializedUserId to $currentUserId - resetting repository")
            realRepository = null
            isInitialPullComplete = false
        }

        if (realRepository == null) {
            console.log("🔄 WebWrapper: Initializing sync-enabled repository...")
            val database = WebDatabaseRepositoryFactory.getDatabaseAsync()
            console.log("✅ WebWrapper: Database initialized")

            // CRITICAL: Use authenticated userId from getUserId callback
            val userId = currentUserId
            console.log("👤 WebWrapper: Using userId: $userId")
            initializedUserId = userId

            val localRepo = SavingsGoalsRepositoryImpl(database, userId)
            val syncService = SavingsGoalsSyncService(
                baseUrl = getBaseHttpUrl(),
                getAuthToken = getAuthToken
            )

            val syncEnabledRepo = SavingsGoalsRepositoryWithSync(
                localRepository = localRepo,
                syncService = syncService,
                userId = userId,
                serverUrl = getWebSocketUrl(),
                // Phase-1 of WS auth: web sends its JWT on connect; mobile
                // stays on the legacy path until its next release ships.
                // TODO(ws-auth-phase-2): same lambda goes into the iOS +
                // Android factories, then we set REQUIRE_WS_AUTH=true on the
                // VPS.
                getAuthToken = getAuthToken,
            )
            realRepository = syncEnabledRepo
            console.log("✅ WebWrapper: Sync-enabled repository created")
            console.log("⏰ WebWrapper: Waiting for WebSocket init block to execute...")

            // WEB PERSISTENCE FIX: Pull data from server to populate fresh in-memory database
            // sql.js creates a fresh database on each page load, so we need to sync from server
            console.log("🔄 WebWrapper: Pulling goals from server to populate local cache...")
            try {
                val pullResult = syncEnabledRepo.getAllGoals(userId)
                when (pullResult) {
                    is DatabaseResult.Success -> {
                        console.log("✅ WebWrapper: Pulled ${pullResult.data.size} goals from server - initial load complete")
                        isInitialPullComplete = true
                    }
                    is DatabaseResult.Error -> {
                        console.warn("⚠️ WebWrapper: Failed to pull from server: ${pullResult.message}")
                        isInitialPullComplete = true // Mark complete even on error to avoid blocking
                    }
                }
            } catch (e: Exception) {
                console.warn("⚠️ WebWrapper: Exception during server pull: ${e.message}")
                isInitialPullComplete = true // Mark complete even on error to avoid blocking
            }
        }
        return realRepository!!
    }

    override suspend fun getActiveGoals(userId: String): DatabaseResult<List<SavingsGoal>> {
        console.log("🔍 WebWrapper: getActiveGoals called for userId=$userId")
        val result = getRepository().getActiveGoals(userId)
        console.log("📊 WebWrapper: getActiveGoals returned ${when(result) {
            is DatabaseResult.Success -> "${result.data.size} goals"
            is DatabaseResult.Error -> "error: ${result.message}"
        }}")
        return result
    }

    override suspend fun getAllGoals(userId: String): DatabaseResult<List<SavingsGoal>> {
        console.log("🔍 WebWrapper: getAllGoals called for userId=$userId")
        return getRepository().getAllGoals(userId)
    }

    override suspend fun getGoalById(goalId: String): DatabaseResult<SavingsGoal?> {
        console.log("🔍 WebWrapper: getGoalById called for goalId=$goalId")
        return getRepository().getGoalById(goalId)
    }

    override suspend fun createGoal(goal: SavingsGoal): DatabaseResult<SavingsGoal> {
        console.log("💾 WebWrapper: createGoal called - name='${goal.name}', target=\$${goal.targetAmount}, id=${goal.id}")
        val result = getRepository().createGoal(goal)
        console.log("✅ WebWrapper: createGoal completed - ${when(result) {
            is DatabaseResult.Success -> "SUCCESS"
            is DatabaseResult.Error -> "ERROR: ${result.message}"
        }}")
        return result
    }

    override suspend fun updateGoal(goal: SavingsGoal): DatabaseResult<Unit> {
        return getRepository().updateGoal(goal)
    }

    override suspend fun updateGoalProgress(goalId: String, currentAmount: Double): DatabaseResult<Unit> {
        return getRepository().updateGoalProgress(goalId, currentAmount)
    }

    override suspend fun updateGoalAiFields(
        goalId: String,
        userId: String,
        accountType: GoalAccountType?,
        debtInterestRate: Double?,
        debtRemainingBalance: Double?,
        debtRemainingMonths: Int?,
        debtPaymentFrequency: PaymentFrequency?,
        debtMonthlyEquivalentPayment: Double?,
    ): DatabaseResult<Unit> = getRepository().updateGoalAiFields(
        goalId, userId, accountType, debtInterestRate, debtRemainingBalance, debtRemainingMonths,
        debtPaymentFrequency, debtMonthlyEquivalentPayment,
    )

    override suspend fun markGoalInactive(goalId: String): DatabaseResult<Unit> {
        return getRepository().markGoalInactive(goalId)
    }

    override suspend fun deleteGoal(goalId: String, userId: String): DatabaseResult<Unit> {
        return getRepository().deleteGoal(goalId, userId)
    }

    override suspend fun getGoalsByCategory(userId: String, category: GoalCategory): DatabaseResult<List<SavingsGoal>> {
        return getRepository().getGoalsByCategory(userId, category)
    }

    override suspend fun getGoalsNearingDeadline(userId: String): DatabaseResult<List<SavingsGoal>> {
        return getRepository().getGoalsNearingDeadline(userId)
    }

    override fun observeActiveGoals(userId: String): Flow<List<SavingsGoal>> {
        console.log("🔄 WebWrapper: observeActiveGoals called for userId=$userId")
        // Delegate to real repository for reactive Flow support
        return kotlinx.coroutines.flow.flow {
            console.log("🔄 WebWrapper: Ensuring repository is initialized before Flow subscription...")
            val repo = getRepository()

            console.log("🔄 WebWrapper: isInitialPullComplete=$isInitialPullComplete")
            // Wait for initial pull to complete before starting Flow subscription (only on first init)
            if (!isInitialPullComplete) {
                console.log("⏳ WebWrapper: Waiting for initial server pull to complete...")
                while (!isInitialPullComplete) {
                    kotlinx.coroutines.delay(100) // Poll every 100ms
                }
                console.log("✅ WebWrapper: Initial pull complete, starting Flow subscription")
            } else {
                console.log("✅ WebWrapper: Repository already initialized, starting Flow subscription immediately")
            }

            repo.observeActiveGoals(userId).collect { goals ->
                console.log("📊 WebWrapper: Flow emitted ${goals.size} goals")
                emit(goals)
            }
        }
    }

    override suspend fun saveRecommendations(recommendations: List<SavingsRecommendation>, userId: String): DatabaseResult<Unit> {
        return getRepository().saveRecommendations(recommendations, userId)
    }

    override suspend fun getRecommendationsForGoal(goalId: String): DatabaseResult<List<SavingsRecommendation>> {
        return getRepository().getRecommendationsForGoal(goalId)
    }

    override suspend fun getUnappliedRecommendations(goalId: String): DatabaseResult<List<SavingsRecommendation>> {
        return getRepository().getUnappliedRecommendations(goalId)
    }

    override suspend fun markRecommendationApplied(recommendationId: Long): DatabaseResult<Unit> {
        return getRepository().markRecommendationApplied(recommendationId)
    }

    override suspend fun deleteRecommendationsForGoal(goalId: String): DatabaseResult<Unit> {
        return getRepository().deleteRecommendationsForGoal(goalId)
    }

    override suspend fun recordContribution(goalId: String, userId: String, amount: Double, date: LocalDate, notes: String?): DatabaseResult<Unit> {
        return getRepository().recordContribution(goalId, userId, amount, date, notes)
    }

    override suspend fun getProgressHistory(goalId: String): DatabaseResult<List<GoalContribution>> {
        return getRepository().getProgressHistory(goalId)
    }

    override suspend fun getTotalContributions(goalId: String): DatabaseResult<Double> {
        return getRepository().getTotalContributions(goalId)
    }

    override suspend fun getMonthlyContributions(goalId: String): DatabaseResult<List<MonthlyContribution>> {
        return getRepository().getMonthlyContributions(goalId)
    }

    override suspend fun deleteProgressHistory(goalId: String): DatabaseResult<Unit> {
        return getRepository().deleteProgressHistory(goalId)
    }
}
